import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { PageHeader, EmptyState } from "../components/admin/AdminUI";
import { dmyToIso, formatDmy, maskDmy } from "../utils/dateFormat";

const scholarshipLabel = {
  "need-based": "Opportunity",
  merit: "Leadership",
};

const statusBadge = {
  pending: { bg: "#fff8e1", color: "#d97706", text: "Pending" },
  accepted: { bg: "#e6f9ee", color: "#16a34a", text: "Accepted (Interview)" },
  approved: { bg: "#eff6ff", color: "#2563eb", text: "Approved" },
  rejected: { bg: "#fef2f2", color: "#dc2626", text: "Rejected" },
};

const formatDate = (dateStr) => formatDmy(dateStr) || "-";

// Scholarship discount diputuskan manual oleh admin saat approve, minimal 25%.
const scholarshipDiscountOptions = { 25: "25%", 50: "50%", 75: "75%", 100: "100%" };

const formatRupiah = (n) =>
  typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-";

// status tetap "accepted" selama menunggu bayar (lihat backend
// finalizeExistingMonthlyConversion) — badge ini dipakai untuk menandai
// kondisi tersebut di dalam tab "Accepted" yang sama, bukan tab terpisah.
const paymentStatusBadge = {
  awaiting_payment: { bg: "#fff8e1", color: "#d97706", text: "Awaiting Payment" },
  payment_failed: { bg: "#fef2f2", color: "#dc2626", text: "Payment Failed" },
  paid_needs_review: { bg: "#fef2f2", color: "#dc2626", text: "Needs Review" },
};

const ScholarshipAdminPage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filter, setFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMe, setErrorMe] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  const fetchApplications = async () => {
    setIsLoading(true);
    setSelectedIds([]);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const res = await axios.get("/scholarships", { params });
      setApplications(res.data);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/scholarshipadminpage" });
    getMe();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  /* ── Checkbox helpers ── */
  const filtered = filter === "all" ? applications : applications.filter((a) => a.status === filter);
  // Select-all mencakup semua baris yang sedang tampil, termasuk pending/paid —
  // aksi bulk yang benar-benar destruktif (reject) tetap dijaga di backend
  // (rejectScholarship menolak status "approved"), dan delete sudah punya
  // preseden yang sama persis di tombol delete single per-baris.
  const checkable = filtered;
  const allChecked = checkable.length > 0 && checkable.every((a) => selectedIds.includes(a.id));
  const someChecked = checkable.some((a) => selectedIds.includes(a.id));

  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? checkable.map((a) => a.id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  /* ── See Details ── */
  const handleSeeDetails = (app) => {
    const badge = statusBadge[app.status] || statusBadge.pending;
    const row = (label, value, extra = "") =>
      value ? `<tr style="border-bottom:1px solid #f5f5f5;"><td style="padding:7px 0;color:#999;width:140px;font-weight:600;vertical-align:top;">${label}</td><td style="padding:7px 0;color:#222;line-height:1.5;${extra}">${value}</td></tr>` : "";
    const section = (title) =>
      `<tr><td colspan="2" style="padding:14px 0 6px;font-size:12px;font-weight:700;color:#fb3104;text-transform:uppercase;letter-spacing:0.5px;">${title}</td></tr>`;

    Swal.fire({
      title: `<span style="font-size:17px; font-weight:700;">${app.name}</span>`,
      html: `
        <div style="text-align:left; font-size:13px; max-height:70vh; overflow-y:auto; padding-right:4px;">
          <div style="margin-bottom:12px;">
            <span style="display:inline-block;padding:3px 12px;border-radius:20px;background:#fff0ee;color:#fb3104;font-weight:700;font-size:12px;">${scholarshipLabel[app.scholarshipType] || app.scholarshipType}</span>
            <span style="display:inline-block;margin-left:6px;padding:3px 12px;border-radius:20px;background:${badge.bg};color:${badge.color};font-weight:700;font-size:12px;">${badge.text}</span>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            ${section("Personal Info")}
            ${row("Email", app.email)}
            ${row("WhatsApp", app.phoneNumber)}
            ${row("Date of Birth", formatDmy(app.dateOfBirth))}
            ${row("Address", app.address)}
            ${row("City", app.city)}
            ${row("Province", app.province)}
            ${row("Postal Code", app.postalCode)}
            ${row("Church", app.localChurch)}
            ${section("Registration")}
            ${row("Class Type", app.classType)}
            ${row("Package", app.studentPackage)}
            ${row("T-Shirt", app.tshirtSize)}
            ${row("Last DJ", app.lastDj)}
            ${row("Role", app.role)}
            ${row("Payment Plan", app.paymentPlan === "installment" ? "Installment" : "Full Payment")}
            ${row("Date Applied", formatDate(app.createdAt))}
            ${(app.status === "accepted" || app.status === "approved") && app.interviewDate ? row("Interview", `${formatDate(app.interviewDate)} · ${app.interviewTime} WIB`, "color:#16a34a;font-weight:600;") : ""}
            ${app.status === "rejected" && app.adminNotes ? row("Admin Notes", app.adminNotes, "color:#dc2626;") : ""}
            ${app.question1 ? `${section("Spiritual Questions")}
            ${row("Q1", app.question1)}
            ${row("Q2", app.question2)}
            ${row("Q3", app.question3)}
            ${row("Q4", app.question4)}
            ${row("Q5", app.question5)}` : ""}
            ${app.scholarshipQ1 ? `${section("Scholarship Questions")}
            ${row("Q1", app.scholarshipQ1)}
            ${row("Q2", app.scholarshipQ2)}
            ${row("Q3", app.scholarshipQ3)}
            ${row("Q4", app.scholarshipQ4)}
            ${row("Q5", app.scholarshipQ5)}
            ${row("Q6", app.scholarshipQ6)}
            ${row("Q7", app.scholarshipQ7)}` : ""}
            ${app.referenceName ? `${section("Reference Contact")}
            ${row("Name", app.referenceName)}
            ${row("Contact Number", app.referenceContact)}` : ""}
          </table>
        </div>
      `,
      confirmButtonColor: "#fb3104",
      confirmButtonText: "Close",
      background: "white",
      color: "black",
      width: "560px",
    });
  };

  /* ── Accept ── */
  const handleAccept = async (app) => {
    const { value: formValues } = await Swal.fire({
      title: "Set Interview Schedule",
      html: `
        <div style="text-align:left; margin-bottom:14px;">
          <label style="font-size:13px; font-weight:600; color:#555; display:block; margin-bottom:4px;">Interview Date</label>
          <input type="text" id="swal-date" class="swal2-input" inputmode="numeric" maxlength="10" placeholder="dd/mm/yyyy" autocomplete="off" style="margin:0; width:100%;" />
        </div>
        <div style="text-align:left;">
          <label style="font-size:13px; font-weight:600; color:#555; display:block; margin-bottom:4px;">Interview Time</label>
          <input type="time" id="swal-time" class="swal2-input" style="margin:0; width:100%;" />
        </div>
      `,
      confirmButtonColor: "#16a34a",
      confirmButtonText: "Accept & Send Email",
      cancelButtonColor: "#aaa",
      showCancelButton: true,
      focusConfirm: false,
      background: "white",
      color: "black",
      didOpen: () => {
        const dateEl = document.getElementById("swal-date");
        dateEl.addEventListener("input", () => {
          dateEl.value = maskDmy(dateEl.value);
        });
      },
      preConfirm: () => {
        // Ditampilkan dd/mm/yyyy, dikirim ke backend tetap yyyy-mm-dd.
        const date = dmyToIso(document.getElementById("swal-date").value);
        const time = document.getElementById("swal-time").value;
        if (!date || !time) {
          Swal.showValidationMessage("Please set both interview date (dd/mm/yyyy) and time.");
          return false;
        }
        return { date, time };
      },
    });

    if (!formValues) return;

    try {
      await axios.patch(`/scholarship/${app.id}/accept`, {
        interviewDate: formValues.date,
        interviewTime: formValues.time,
      });
      Swal.fire({
        text: "Application accepted. Interview schedule email has been sent.",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Approve (post-interview) ── */
  const handleApprove = async (app) => {
    const { value: discountPercent, isConfirmed } = await Swal.fire({
      title: "Approve Scholarship",
      html: `<p style="font-size:14px; color:#444;">Approve <strong>${app.name}</strong>'s scholarship application?<br/>If a payment is required, a payment link will be sent to their email — they only become an official student once payment is confirmed.</p>`,
      icon: "question",
      input: "select",
      inputOptions: scholarshipDiscountOptions,
      inputValue: "25",
      inputLabel: "Scholarship discount",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Approve",
      background: "white",
      color: "black",
    });
    if (!isConfirmed) return;

    try {
      const res = await axios.patch(`/scholarship/${app.id}/approve`, {
        discountPercent: Number(discountPercent),
      });
      const message = res.data.paymentRequired
        ? `Payment link sent to the applicant's email! Amount due: ${formatRupiah(res.data.price?.finalPrice)}`
        : "Scholarship approved. No payment required.";
      Swal.fire({
        text: message,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Reject (notes optional) ── */
  // Perbaiki email applicant yang salah ketik. Backend menentukan sendiri apa
  // yang perlu dikirim ulang ke alamat baru: applicant berstatus "accepted"
  // otomatis menerima ULANG undangan interview dengan jadwal yang sama, dan
  // applicant "approved" yang belum bayar menerima link pembayaran BARU.
  const handleEditEmail = async (app) => {
    const { value: newEmail } = await Swal.fire({
      title: "Edit Applicant Email",
      input: "email",
      inputLabel:
        app.status === "accepted"
          ? `Correct email for ${app.name} — the interview invitation will be re-sent`
          : app.status === "approved"
          ? `Correct email for ${app.name} — a new payment link will be sent`
          : `Correct email for ${app.name}`,
      inputValue: app.email,
      inputPlaceholder: "correct@email.com",
      background: "white",
      color: "black",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Save",
      inputValidator: (v) => (!v ? "Please enter an email address" : undefined),
    });
    if (!newEmail || newEmail.trim().toLowerCase() === (app.email || "").toLowerCase()) return;

    try {
      const res = await axios.patch(`/scholarship/${app.id}/email`, { newEmail: newEmail.trim() });
      await Swal.fire({
        text: res.data.msg,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchApplications();
    } catch (err) {
      await Swal.fire({
        text: err.response?.data?.msg || "Failed to update email.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  const handleReject = async (app) => {
    const { value: notes, isConfirmed } = await Swal.fire({
      title: "Reject Application",
      input: "textarea",
      inputLabel: "Notes for the applicant (optional)",
      inputPlaceholder: "Enter your notes here...",
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Reject & Send Email",
      cancelButtonColor: "#aaa",
      showCancelButton: true,
      background: "white",
      color: "black",
    });

    if (!isConfirmed) return;

    try {
      await axios.patch(`/scholarship/${app.id}/reject`, { adminNotes: notes || "" });
      Swal.fire({
        text: "Application rejected. Notification email has been sent.",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Delete single ── */
  const handleDeleteSingle = async (app) => {
    const result = await Swal.fire({
      title: "Delete Application",
      text: `Delete ${app.name}'s scholarship application?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      background: "white",
      color: "black",
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`/scholarship/${app.id}`);
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Delete selected ── */
  const handleDeleteSelected = async () => {
    const result = await Swal.fire({
      title: "Delete Selected",
      text: `Delete ${selectedIds.length} selected application(s)? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#aaa",
      confirmButtonText: `Delete (${selectedIds.length})`,
      background: "white",
      color: "black",
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete("/scholarships", { data: { ids: selectedIds } });
      setSelectedIds([]);
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Bulk Approve (accepted tab) ── */
  const handleBulkApprove = async () => {
    const { value: discountPercent, isConfirmed } = await Swal.fire({
      title: `Approve ${selectedIds.length} Application(s)`,
      html: `<p style="font-size:14px;color:#444;">Approve all selected applicants? If a payment is required for any of them, a payment link will be sent to their email — they only become official students once payment is confirmed.<br/>The same discount will be applied to all selected applicants.</p>`,
      icon: "question",
      input: "select",
      inputOptions: scholarshipDiscountOptions,
      inputValue: "25",
      inputLabel: "Scholarship discount (applies to all selected)",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#aaa",
      confirmButtonText: `Approve (${selectedIds.length})`,
      background: "white",
      color: "black",
    });
    if (!isConfirmed) return;

    try {
      const results = await Promise.all(
        selectedIds.map((id) =>
          axios.patch(`/scholarship/${id}/approve`, { discountPercent: Number(discountPercent) }),
        ),
      );
      const paymentCount = results.filter((r) => r.data.paymentRequired).length;
      const message =
        paymentCount > 0
          ? `${selectedIds.length} application(s) approved — ${paymentCount} payment link(s) sent by email.`
          : `${selectedIds.length} scholarship(s) approved. No payment required.`;
      Swal.fire({
        text: message,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      setSelectedIds([]);
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  /* ── Bulk Reject (pending) ── */
  const handleBulkReject = async () => {
    const { value: notes, isConfirmed } = await Swal.fire({
      title: `Reject ${selectedIds.length} Application(s)`,
      input: "textarea",
      inputLabel: "Notes for applicants (optional)",
      inputPlaceholder: "Enter your notes here...",
      confirmButtonColor: "#dc2626",
      confirmButtonText: `Reject (${selectedIds.length}) & Send Email`,
      cancelButtonColor: "#aaa",
      showCancelButton: true,
      background: "white",
      color: "black",
    });

    if (!isConfirmed) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.patch(`/scholarship/${id}/reject`, { adminNotes: notes || "" })
        )
      );
      Swal.fire({
        text: `${selectedIds.length} application(s) rejected. Notification emails have been sent.`,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });
      setSelectedIds([]);
      fetchApplications();
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Something went wrong.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    }
  };

  const STATUS_CLASS = {
    pending: "adm-status--warning",
    accepted: "adm-status--success",
    approved: "adm-status--info",
    rejected: "adm-status--danger",
  };
  const PAYMENT_CLASS = {
    awaiting_payment: "adm-status--warning",
    payment_failed: "adm-status--danger",
    paid_needs_review: "adm-status--danger",
  };
  const tabCount = (val) => (val === "all" ? applications.length : applications.filter((a) => a.status === val).length);

  return (
    <div className="adm-main">
      <PageHeader
        title="Scholarship Applications"
        subtitle="Review applications: accept for interview, then approve to send the payment link."
      />

      <div className="adm-tabs">
        {["all", "pending", "accepted", "approved", "rejected"].map((val) => (
          <button
            key={val}
            type="button"
            className={`adm-tab${filter === val ? " adm-tab--active" : ""}`}
            onClick={() => setFilter(val)}
          >
            {val === "all" ? "All" : val === "accepted" ? "Accepted (Interview)" : val.charAt(0).toUpperCase() + val.slice(1)}
            <span className="adm-tab-count">{tabCount(val)}</span>
          </button>
        ))}
      </div>

      {selectedIds.length > 0 && (
        <div className="adm-bulk-bar">
          <span>
            <strong>{selectedIds.length}</strong> selected
          </span>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setSelectedIds([])}>
              Clear selection
            </button>
            {filter === "accepted" ? (
              <>
                <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={handleBulkReject}>
                  <i className="bi bi-x-lg"></i>
                  Reject ({selectedIds.length})
                </button>
                <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={handleBulkApprove}>
                  <i className="bi bi-check-lg"></i>
                  Approve ({selectedIds.length})
                </button>
              </>
            ) : (
              <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={handleDeleteSelected}>
                <i className="bi bi-trash"></i>
                Delete selected ({selectedIds.length})
              </button>
            )}
          </div>
        </div>
      )}

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading applications…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="bi-mortarboard" title="No applications here" text="There is nothing in this category yet." />
        ) : (
          <div className="adm-table-wrap adm-table-wrap--scroll">
            <table className="adm-table" style={{ minWidth: "980px" }}>
              <thead>
                <tr>
                  <th style={{ width: "44px" }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                      onChange={handleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Applicant</th>
                  <th>Scholarship</th>
                  <th>Church & City</th>
                  <th>Applied</th>
                  <th>Status</th>
                  <th className="adm-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((app) => {
                  const badge = statusBadge[app.status] || statusBadge.pending;
                  const payment = paymentStatusBadge[app.paymentStatus];
                  const isChecked = selectedIds.includes(app.id);
                  return (
                    <tr key={app.id} className={isChecked ? "adm-row-selected" : ""}>
                      <td>
                        <input type="checkbox" checked={isChecked} onChange={() => handleSelectOne(app.id)} aria-label={`Select ${app.name}`} />
                      </td>
                      <td>
                        <div className="adm-cell-title">{app.name}</div>
                        <div className="adm-cell-meta">{app.email}</div>
                        <div className="adm-cell-meta">{app.phoneNumber}</div>
                      </td>
                      <td>
                        <span className="adm-badge adm-badge--purple">{scholarshipLabel[app.scholarshipType] || app.scholarshipType}</span>
                      </td>
                      <td>
                        <div>{app.localChurch || "-"}</div>
                        <div className="adm-cell-meta">{app.city || "-"}</div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>{formatDate(app.createdAt)}</td>
                      <td>
                        <div className="d-flex flex-column align-items-start gap-1">
                          <span className={`adm-status ${STATUS_CLASS[app.status] || STATUS_CLASS.pending}`}>{badge.text}</span>
                          {payment && <span className={`adm-status ${PAYMENT_CLASS[app.paymentStatus]}`}>{payment.text}</span>}
                        </div>
                      </td>
                      <td className="adm-col-actions">
                        <div className="adm-actions">
                          <button type="button" className="adm-icon-btn" title="See details" aria-label="See details" onClick={() => handleSeeDetails(app)}>
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn"
                            aria-label="Edit email"
                            onClick={() => handleEditEmail(app)}
                            title={
                              app.status === "accepted"
                                ? "Edit email — the interview invitation is re-sent automatically"
                                : app.status === "approved"
                                ? "Edit email — the old payment link is cancelled and a new one is sent"
                                : "Edit email"
                            }
                          >
                            <i className="bi bi-envelope"></i>
                          </button>
                          {app.status === "pending" && (
                            <>
                              <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={() => handleReject(app)}>
                                Reject
                              </button>
                              <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => handleAccept(app)}>
                                Accept
                              </button>
                            </>
                          )}
                          {app.status === "accepted" && (
                            <>
                              <button type="button" className="adm-btn adm-btn--danger adm-btn--sm" onClick={() => handleReject(app)}>
                                Reject
                              </button>
                              {app.paymentStatus !== "awaiting_payment" && (
                                <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => handleApprove(app)}>
                                  Approve
                                </button>
                              )}
                            </>
                          )}
                          {app.status !== "accepted" && app.status !== "pending" && (
                            <button
                              type="button"
                              className="adm-icon-btn adm-icon-btn--danger"
                              title="Delete"
                              aria-label="Delete application"
                              onClick={() => handleDeleteSingle(app)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="adm-table-footer">
          <span>
            <strong style={{ color: "var(--adm-text)" }}>{filtered.length}</strong> application{filtered.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScholarshipAdminPage;
